<script>
import { required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { useDebounceFn } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { ref, computed, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Icon from 'next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: { Spinner, Switch, Icon, NextButton },
  props: {
    selectedKnowledgebase: {
      type: Object,
      default: () => {},
    },
  },
  setup(props, { emit }) {
    const rules = {
      url: { required },
      regex: {},
    };

    const { t } = useI18n();

    const store = useStore();
    const url = ref('');
    const urls = ref([]);
    const selectedUrls = ref([]);
    const previousUrl = ref('');
    const previousRegex = ref('');
    const uiFlags = computed(() => store.getters['sitemaps/getUIFlags']);
    const pageTitle = computed(() => t('LIBRARIES.SITES.TITLE'));
    const name = ref('');
    const description = ref('');
    const autoupdate = ref(true);
    const customized = ref(false);
    const regex = ref('');
    const showAdvancedSettings = ref(false);

    const v$ = useVuelidate(rules, { url });

    const hasUrls = computed(() => urls.value.length > 0);

    const fetchUrls = useDebounceFn(async () => {
      if (
        url.value &&
        (url.value !== previousUrl.value || regex.value !== previousRegex.value)
      ) {
        previousUrl.value = url.value;
        previousRegex.value = regex.value;
        try {
          const fetchedUrls = await store.dispatch('sitemaps/getUrls', {
            knowledgebaseId: props.selectedKnowledgebase.id,
            siteUrl: url.value,
            regex: regex.value,
          });
          urls.value = fetchedUrls;
          selectedUrls.value = fetchedUrls.map(_url => _url);
        } catch (error) {
          useAlert(t('LIBRARIES.SITES.API.ERROR_MESSAGE'));
        }
      }
    }, 1000);

    watch(url, fetchUrls, { immediate: true });
    watch(regex, fetchUrls, { immediate: true });

    const onClose = () => {
      emit('on-close');
    };

    const addSite = async () => {
      v$.value.$touch();
      if (v$.value.$invalid) {
        return;
      }
      try {
        await store.dispatch('sitemaps/create', {
          knowledgebaseId: props.selectedKnowledgebase.id,
          sitemap: {
            url: url.value,
            urls: selectedUrls.value,
            autoupdate: autoupdate.value,
            customized: customized.value,
            regex: regex.value,
          },
        });
        useAlert(t('LIBRARIES.SITES.API.SUCCESS_MESSAGE'));
        onClose();
      } catch (error) {
        useAlert(t('LIBRARIES.SITES.API.ERROR_MESSAGE'));
      }
    };

    const selectAll = () => {
      selectedUrls.value = urls.value.map(url => url);
      customized.value = true;
    };

    const deselectAll = () => {
      selectedUrls.value = [];
      customized.value = true;
    };

    const toggleSelectAll = () => {
      if (selectedUrls.value.length === urls.value.length) {
        selectedUrls.value = [];
        customized.value = true;
      } else {
        selectedUrls.value = urls.value.map(url => url);
        customized.value = true;
      }
    };

    watch(selectedUrls, (newVal, oldVal) => {
      if (newVal.length !== urls.value.length) {
        customized.value = true;
      } else {
        customized.value = false;
      }
    });

    return {
      v$,
      url,
      urls,
      selectedUrls,
      uiFlags,
      pageTitle,
      onClose,
      addSite,
      name,
      description,
      selectAll,
      deselectAll,
      hasUrls,
      toggleSelectAll,
      autoupdate,
      customized,
      regex,
      showAdvancedSettings,
    };
  },
  mounted() {
    this.setFormValues();
    if (this.url) {
      this.fetchUrls();
    }
    this.$nextTick(() => {
      const el = this.$refs.firstInput?.$el?.querySelector?.('input');
      if (el) el.focus();
    });
  },
  methods: {
    setFormValues() {
      const { name, description } = this.selectedKnowledgebase;
      this.name = name;
      this.description = description;
    },
  },
};
</script>

<template>
  <div class="h-auto overflow-auto flex flex-col">
    <woot-modal-header :header-title="pageTitle" />

    <div class="flex flex-col w-full mt-2 px-8">
      <p>
        {{ $t('LIBRARIES.SITES.ADD.DESCRIPTION') }}
      </p>
    </div>

    <form
      class="flex flex-col w-full"
      @submit.prevent="addSite"
      @keydown.enter.prevent="addSite"
    >
      <div class="w-full">
        <woot-input
          ref="firstInput"
          v-model="url"
          data-testid="site-url-input"
          :label="$t('LIBRARIES.SITES.ADD.FORM.URL.LABEL')"
          type="text"
          class="mb-0"
          :class="{ error: v$.url.$error }"
          :error="v$.url.$error ? $t('LIBRARIES.SITES.ADD.FORM.URL.ERROR') : ''"
          :placeholder="$t('LIBRARIES.SITES.ADD.FORM.URL.PLACEHOLDER')"
          @blur="v$.url.$touch"
        />
      </div>
      <div class="w-full mb-4">
        <label class="flex items-center">
          <span class="mr-2">{{
            $t('LIBRARIES.SITES.ADD.FORM.AUTOUPDATE.LABEL')
          }}</span>
          <Switch v-model="autoupdate" />
        </label>
      </div>
      <div class="w-full">
        <NextButton
          data-testid="site-advanced-settings-toggle"
          :label="$t('LIBRARIES.SITES.ADD.FORM.ADVANCED_SETTINGS.TOGGLE')"
          :icon="{
            'i-lucide-chevron-down': !showAdvancedSettings,
            'i-lucide-chevron-up': showAdvancedSettings,
          }"
          sm
          link
          @click.prevent="showAdvancedSettings = !showAdvancedSettings"
        />
      </div>

      <div v-if="showAdvancedSettings" class="w-full my-4">
        <woot-input
          v-model="regex"
          data-testid="site-regex-input"
          :label="$t('LIBRARIES.SITES.ADD.FORM.REGEX.LABEL')"
          type="text"
          :placeholder="$t('LIBRARIES.SITES.ADD.FORM.REGEX.PLACEHOLDER')"
        />
        <p class="text-sm text-gray-500">
          {{ $t('LIBRARIES.SITES.ADD.FORM.REGEX.DESCRIPTION') }}
        </p>
      </div>

      <div v-if="uiFlags.isFetchingUrls">
        <Spinner :size="48" />
      </div>
      <div v-else-if="hasUrls" class="w-full my-4">
        <div class="max-h-48 overflow-y-scroll">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider flex flex-row gap-2"
                >
                  <input
                    type="checkbox"
                    :checked="selectedUrls.length === urls.length"
                    @change="toggleSelectAll"
                  />
                  <span>Select</span>
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Page URL
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="url in urls" :key="url.loc">
                <td class="px-6 py-4 whitespace-nowrap">
                  <input
                    :id="'url-' + url.loc"
                    v-model="selectedUrls"
                    type="checkbox"
                    :value="url"
                  />
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <label
                    :for="'url-' + url.loc"
                    class="flex-1 overflow-hidden text-ellipsis"
                  >
                    <a
                      :href="url.loc"
                      rel="noopener noreferrer"
                      target="_blank"
                      >{{ url.loc }}</a
                    >
                  </label>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="flex flex-row justify-end gap-2 py-2 px-0 w-full">
        <NextButton
          :label="$t('LIBRARIES.SITES.CANCEL_BUTTON_TEXT')"
          md
          ghost
          slate
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          data-testid="site-submit-button"
          :is-loading="uiFlags.isCreating"
          :label="$t('LIBRARIES.SITES.CREATE_BUTTON_TEXT')"
        />
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
::v-deep .ProseMirror-woot-style {
  height: 5rem;
}
</style>
